import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { GET_MONTHLY_SIGNUP_STATS } from '../../apollo/queries';
import '../css/MonthlyStatsPage.css';

const MonthlyStatsPage = () => {
    const navigate = useNavigate();
    const [selectedYear, setSelectedYear] = useState(2025);

    // GraphQL 쿼리 실행
    const { data, loading, error, refetch } = useQuery(GET_MONTHLY_SIGNUP_STATS, {
        variables: { year: selectedYear },
        errorPolicy: 'all'
    });

    // 년도 변경 시 쿼리 재실행
    useEffect(() => {
        refetch({ year: selectedYear });
    }, [selectedYear, refetch]);

    // 로딩 중일 때
    if (loading) {
        return (
            <>
                <div className="breadcrumb">
                    <span>홈 &gt; 회원관리 &gt; 월별 가입 통계</span>
                </div>
                <div className="admin-main-content">
                    <main className="admin-content">
                        <div className="page-header">
                            <div className="page-title-bar"></div>
                            <h2>월별 가입 통계</h2>
                        </div>
                        <div style={{ textAlign: 'center', padding: '50px' }}>
                            <div>월별 가입 통계를 불러오는 중입니다...</div>
                        </div>
                    </main>
                </div>
            </>
        );
    }

    // 에러 발생 시
    if (error) {
        return (
            <>
                <div className="breadcrumb">
                    <span>홈 &gt; 회원관리 &gt; 월별 가입 통계</span>
                </div>
                <div className="admin-main-content">
                    <main className="admin-content">
                        <div className="page-header">
                            <div className="page-title-bar"></div>
                            <h2>월별 가입 통계</h2>
                        </div>
                        <div style={{ textAlign: 'center', padding: '50px', color: '#ff4444' }}>
                            <div>데이터를 불러오는 중 오류가 발생했습니다: {error.message}</div>
                        </div>
                    </main>
                </div>
            </>
        );
    }

    const statsData = data?.monthlySignupStats || [];
    const totalSignups = statsData.reduce((sum, stat) => sum + stat.totalSignups, 0);
    const maxSignups = Math.max(...statsData.map(stat => stat.totalSignups), 1);

    const handleYearChange = (e) => {
        setSelectedYear(parseInt(e.target.value));
    };

    const handleSearch = () => {
        refetch({ year: selectedYear });
    };

    return (
        <>
            <div className="breadcrumb">
                <span>홈 &gt; 회원관리 &gt; 월별 가입 통계</span>
            </div>
            <div className="admin-main-content">
                <aside className="admin-sidebar">
                    <div className="sidebar-menu">
                        <h3>회원 정보 관리</h3>
                        <ul>
                            <li onClick={() => navigate('/admin/customer')}>회원 정보 관리</li>
                            <li onClick={() => navigate('/admin/customer/register')}>회원 등록하기</li>
                            <li onClick={() => navigate('/admin/customer/blocked')}>차단 회원 관리</li>
                        </ul>
                    </div>
                    <div className="sidebar-menu">
                        <h3>가입통계</h3>
                        <ul>
                            <li className="active">월별 가입 통계</li>
                        </ul>
                    </div>
                </aside>
                <main className="admin-content">
                    <div className="page-header">
                        <div className="page-title-bar"></div>
                        <h2>월별 가입 통계</h2>
                    </div>
                    <div className="search-filters">
                        <div className="filter-group">
                            <label>기간 검색</label>
                            <div className="search-field">
                                <select value={selectedYear} onChange={handleYearChange}>
                                    <option value={2023}>2023</option>
                                    <option value={2024}>2024</option>
                                    <option value={2025}>2025</option>
                                    <option value={2026}>2026</option>
                                </select>
                            </div>
                        </div>
                        <div className="filter-group">
                            <label>레벨 검색</label>
                            <div className="level-options">
                                <label><input type="checkbox" checked readOnly /> 전체</label>
                                <label><input type="checkbox" /> A급</label>
                                <label><input type="checkbox" /> B급</label>
                                <label><input type="checkbox" /> C급</label>
                                <label><input type="checkbox" /> D급</label>
                            </div>
                        </div>
                    </div>
                    <button className="search-button" onClick={handleSearch}>검 색</button>

                    <div className="stats-list-container">
                        <div className="stats-list-header">
                            <span>총 회원 수 : {totalSignups} 명</span>
                            <button className="excel-button">엑셀 저장</button>
                        </div>
                        <table className="stats-table">
                            <thead>
                                <tr>
                                    <th>날짜</th>
                                    <th>그래프</th>
                                    <th>비율%</th>
                                    <th>전체</th>
                                    <th>A급</th>
                                    <th>B급</th>
                                    <th>C급</th>
                                    <th>D급</th>
                                </tr>
                            </thead>
                            <tbody>
                                {statsData.map((data) => (
                                    <tr key={data.month}>
                                        <td data-label="날짜">{data.month}</td>
                                        <td data-label="그래프">
                                            <div className="progress-bar-container">
                                                <div className="progress-bar" style={{ width: `${(data.totalSignups / maxSignups) * 100}%` }}></div>
                                            </div>
                                        </td>
                                        <td data-label="비율%">{data.percentage}%</td>
                                        <td data-label="전체">{data.totalSignups}</td>
                                        <td data-label="A급">{data.aGradeCount}</td>
                                        <td data-label="B급">{data.bGradeCount}</td>
                                        <td data-label="C급">{data.cGradeCount}</td>
                                        <td data-label="D급">{data.dGradeCount}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        {statsData.length === 0 && (
                            <div style={{ textAlign: 'center', padding: '50px' }}>
                                <div>해당 연도의 가입 통계가 없습니다.</div>
                            </div>
                        )}
                    </div>
                </main>
            </div>
        </>
    );
};

export default MonthlyStatsPage; 